import { FC, FormEvent } from 'react';
import { useAppForm } from '@new/common/hooks/form';
import { formOptions } from '@tanstack/react-form';
import { Link, useNavigate } from '@tanstack/react-router';
import z from 'zod';
import { useSignupMutation } from '@teachur/http-client';
import {
  emailValidation,
  passwordValidation,
  agreementValidation,
} from '../../schemas/validations';
import { toast } from 'sonner';
import { getErrorMessage } from '@new/lib/getErrorMessage';

export const signupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: emailValidation,
  password: passwordValidation,
  agreement: agreementValidation,
});

export const signupFormOpts = formOptions({
  defaultValues: {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    agreement: false,
  },
});

const CheckboxLabel = () => (
  <label className="text-[0.75rem] xl:text-[14px] text-(--neutral-white-NW600)">
    By clicking “Create account”, you acknowledge that you have read and understood and agree to
    Newlane&nbsp;
    <a
      href="/agreements/termsOfUse"
      target="_blank"
      className="semibold m-1 ml-0 [font-family:var(--font-montserrat)]! text-(--neutral-black-NB1000)! underline!"
    >
      Terms and Conditions
    </a>
    and
    <a
      href="/agreements/privacyPolicy"
      target="_blank"
      className="semibold m-1 [font-family:var(--font-montserrat)]! text-(--neutral-black-NB1000)! underline!"
    >
      Privacy Policy
    </a>
  </label>
);

export const SignupForm: FC = () => {
  const navigate = useNavigate();
  const { mutateAsync: signup } = useSignupMutation({
    onSuccess: () => {
      navigate({ to: '/confirm-email' });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const form = useAppForm({
    ...signupFormOpts,
    validators: {
      onChange: ({ value }) => {
        const parsed = signupSchema.safeParse(value);

        if (parsed.success) {
          return { fields: {} };
        }

        const errors: Record<string, string> = {};
        parsed.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (typeof field === 'string') {
            errors[field] = issue.message;
          }
        });

        return { fields: errors };
      },

      onSubmit: ({ value }) => {
        const parsed = signupSchema.safeParse(value);
        if (!parsed.success) {
          const errors: Record<string, string> = {};

          parsed.error.issues.forEach((issue) => {
            const field = issue.path[0];
            if (typeof field === 'string') {
              errors[field] = issue.message;
            }
          });

          return { fields: errors };
        }

        signup(parsed.data);
      },
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <>
      <div className="w-full flex flex-col justify-between items-center gap-8">
        <div className="flex flex-col justify-center items-center gap-2">
          <h1 className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h3)]! md:[font-size:var(--fs-h2)]! xl:[font-size:var(--fs-h1)]! leading-(--lh-h)! font-bold! m-0! text-(--neutral-black-NB1000)!">
            Create your account
          </h1>
          <p className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-p3)]! leading-tight text-center! font-medium! m-0! text-(--neutral-black-NB900)!">
            Please enter details below to create your account.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="w-full flex flex-1 flex-col"
          data-test-id="signup-form"
        >
          <div className="flex flex-col">
            <div className="block md:flex md:gap-3 w-full justify-start items-center">
              <form.AppField
                name="firstName"
                children={(field) => (
                  <field.Text label="First Name" placeholder="First Name" className="flex-1" />
                )}
              />

              <form.AppField
                name="lastName"
                children={(field) => (
                  <field.Text label="Last Name" placeholder="Last Name" className="flex-1" />
                )}
              />
            </div>

            <form.AppField
              name="email"
              children={(field) => (
                <field.Email label="Email" placeholder="Enter your email address" />
              )}
            />

            <form.AppField
              name="password"
              children={(field) => (
                <field.Password label="Password" placeholder="Enter your password" />
              )}
            />
          </div>

          <div className="flex flex-col gap-10 ">
            <form.AppField
              name="agreement"
              children={(field) => (
                <field.Checkbox label="" labelPlacement="right" renderLabel={CheckboxLabel} />
              )}
            />

            <form.AppForm>
              <form.SubmitButton label="Create account" />
            </form.AppForm>
          </div>
        </form>
      </div>
      <div className="flex items-center gap-1 self-center">
        <p className="[font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--neutral-white-NW600) m-0!">
          Already have an account?
        </p>
        <Link
          to="/login"
          className="bold underline! [font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--primary-O900)!"
        >
          Login
        </Link>
      </div>
    </>
  );
};
